"use client";

import { AnimatePresence, motion } from "motion/react";
import {
  IconBuilding,
  IconWorld,
  IconSparkles,
  IconTarget,
  IconUser,
} from "@tabler/icons-react";
import {
  Badge,
  Box,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { AVATARS, computeProgress } from "./utils";

function ProgressRing({ value }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;

  return (
    <Box
      pos="relative"
      w={72}
      h={72}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <svg
        width={128}
        height={128}
        style={{ transform: "rotate(-90deg)" }}
        viewBox="0 0 64 64"
      >
        <defs>
          <linearGradient id="canvaRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--mantine-color-cyan-5)" />
            <stop offset="55%" stopColor="var(--mantine-color-violet-5)" />
            <stop offset="100%" stopColor="var(--mantine-color-grape-5)" />
          </linearGradient>
        </defs>
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke="var(--mantine-color-gray-2)"
          strokeWidth="5"
        />
        <motion.circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke="url(#canvaRing)"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={c}
          animate={{ strokeDashoffset: offset }}
          transition={{ type: "spring", stiffness: 90, damping: 18 }}
        />
      </svg>
      <Text pos="absolute" size="md" fw={600}>
        {value}%
      </Text>
    </Box>
  );
}

function Field({ icon, label, value, placeholder }) {
  const filled = Boolean(value);

  return (
    <Paper
      withBorder
      radius="xl"
      p={"md"}
      px="md"
      bg="rgba(0, 0, 0, 0.02)" // Très léger gris pour correspondre au bg-secondary/40
    >
      <Group wrap="nowrap" gap="md">
        <ThemeIcon size={32} radius="md" variant="light" color="primary">
          {icon}
        </ThemeIcon>

        <Box style={{ minWidth: 0 }}>
          <Text size="md" fw={600} tt="uppercase" c="dimmed" lts={1}>
            {label}
          </Text>

          <AnimatePresence mode="wait">
            <motion.div
              key={value || "empty"}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
            >
              <Text size="md" fw={500} truncate c={filled ? "dark" : "dimmed"}>
                {filled ? value : placeholder}
              </Text>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Group>
    </Paper>
  );
}

export function AgentPreviewCard({ draft }) {
  const avatar = AVATARS.find((a) => a.id === draft.avatarId);
  const progress = computeProgress(draft);

  return (
    <Paper
      radius={32}
      p="xl"
      withBorder
      pos="relative"
      style={{
        overflow: "hidden",
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 24px 60px -30px rgba(30,41,120,0.35)",
      }}
    >
      {/* Tâches de couleur floutées en arrière-plan */}
      <Box
        pos="absolute"
        top={-64}
        right={-64}
        w={192}
        h={192}
        style={{
          borderRadius: "50%",
          backgroundColor: "var(--mantine-color-primary-1)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />
      <Box
        pos="absolute"
        bottom={-80}
        left={-40}
        w={192}
        h={192}
        style={{
          borderRadius: "50%",
          backgroundColor: "var(--mantine-color-primary-2)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />

      {/* En-tête : Badge et Anneau de progression */}
      <Group justify="space-between" align="center" pos="relative">
        <Badge
          variant="light"
          color="primary"
          size="lg"
          radius="xl"
          leftSection={<IconSparkles size={14} />}
          style={{ textTransform: "none" }}
        >
          Agent en construction
        </Badge>
        <ProgressRing value={progress} />
      </Group>

      {/* Profil de l'agent */}
      <Group gap="md" mt="xl" pos="relative" wrap="nowrap">
        <Box pos="relative">
          <Box
            w={80}
            h={80}
            style={{
              borderRadius: 24,
              border: `2px solid ${
                avatar
                  ? `var(--mantine-color-${avatar.accent}-5)`
                  : "var(--mantine-color-gray-3)"
              }`,
              backgroundColor: "var(--mantine-color-gray-1)",
              overflow: "hidden",
              display: "grid",
              placeItems: "center",
              boxShadow: "var(--mantine-shadow-sm)",
            }}
          >
            <AnimatePresence mode="wait">
              {avatar ? (
                <motion.img
                  key={avatar.id}
                  src={avatar.src || "/placeholder.svg"}
                  alt={`Avatar de ${draft.name || "l'agent"}`}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <motion.div
                  key="ph"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{ color: "var(--mantine-color-gray-4)" }}
                >
                  <IconUser size={32} />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          {/* Point vert de progression */}
          {progress > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              style={{
                position: "absolute",
                bottom: -4,
                right: -4,
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "2px solid white",
                backgroundColor: "var(--mantine-color-green-4)",
              }}
            />
          )}
        </Box>

        <Box style={{ minWidth: 0 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={draft.name || "noname"}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
            >
              <Text size="xl" fw={600} truncate>
                {draft.name || "Votre agent"}
              </Text>
            </motion.div>
          </AnimatePresence>
          <Text size="lg" c="dimmed" truncate>
            {draft.role ? `${draft.role} IA` : "Rôle en cours de définition"}
          </Text>
          {draft.tone && (
            <Badge
              mt={4}
              variant="light"
              color="gray"
              radius="xl"
              style={{ textTransform: "none" }}
            >
              {draft.tone}
            </Badge>
          )}
        </Box>
      </Group>

      {/* Liste des champs */}
      <Stack gap="sm" mt="xl" pos="relative">
        <Field
          icon={<IconBuilding size={16} />}
          label="Entreprise"
          value={draft.companyName}
          placeholder="En attente d'analyse"
        />
        <Field
          icon={<IconTarget size={16} />}
          label="Promesse"
          value={draft.chosenValueProp}
          placeholder="À sélectionner"
        />
        <Field
          icon={<IconWorld size={16} />}
          label="Langue"
          value={draft.language}
          placeholder="À définir"
        />
      </Stack>
    </Paper>
  );
}
